import React, { useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Badge,
  Button,
  Card,
  Dropdown,
  Empty,
  Modal,
  Progress,
  Space,
  Table,
  Tag,
  Tooltip,
  Typography,
} from 'antd';
import {
  BarChartOutlined,
  CopyOutlined,
  DeleteOutlined,
  EditOutlined,
  ExperimentOutlined,
  EyeOutlined,
  MoreOutlined,
  PauseCircleOutlined,
  PlayCircleOutlined,
  PlusOutlined,
  SendOutlined,
  TrophyOutlined,
} from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import type { MenuProps } from 'antd';
import { useAppDispatch, useAppSelector } from 'store';
import {
  deleteABTest,
  duplicateABTest,
  fetchABTests,
  pauseABTest,
  selectABTestLoading,
  selectABTestPage,
  selectABTestPageSize,
  selectABTestSending,
  selectABTestStatusFilter,
  selectABTests,
  selectABTestsTotal,
  sendABTestPush,
  setPage,
  setStatusFilter,
  startABTest,
} from 'store/slices/abTest.slice';
import { usePermissions } from 'hooks/usePermissions';
import { ABTestListItem, ABTestStatus } from 'types/ab_test.types';
import { formatters } from 'utils/formatters';
import { notify } from 'utils/helpers';
import { ROUTES } from 'config/routes';

const { Text } = Typography;
const { confirm } = Modal;

interface Props {
  appId: number | null;
}

const STATUS_META: Record<ABTestStatus, { color: string; label: string }> = {
  [ABTestStatus.DRAFT]: { color: 'default', label: 'Черновик' },
  [ABTestStatus.RUNNING]: { color: 'processing', label: 'Активен' },
  [ABTestStatus.PAUSED]: { color: 'warning', label: 'Пауза' },
  [ABTestStatus.COMPLETED]: { color: 'success', label: 'Завершён' },
  [ABTestStatus.CANCELLED]: { color: 'error', label: 'Отменён' },
};

const fmtInt = (v: number) => (v ?? 0).toLocaleString('ru-RU');

const ABTestList: React.FC<Props> = ({ appId }) => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const permissions = usePermissions();

  const tests = useAppSelector(selectABTests);
  const total = useAppSelector(selectABTestsTotal);
  const page = useAppSelector(selectABTestPage);
  const pageSize = useAppSelector(selectABTestPageSize);
  const statusFilter = useAppSelector(selectABTestStatusFilter);
  const loading = useAppSelector(selectABTestLoading);
  const sending = useAppSelector(selectABTestSending);
  const reload = useCallback(() => {
    if (!appId) return;
    dispatch(
      fetchABTests({
        appId,
        status: statusFilter,
        limit: pageSize,
        offset: (page - 1) * pageSize,
      })
    );
  }, [dispatch, appId, statusFilter, page, pageSize]);

  useEffect(() => {
    reload();
  }, [reload]);

  const run = async (thunk: any, okMsg: string) => {
    try {
      await dispatch(thunk).unwrap();
      notify.success(okMsg);
      reload();
    } catch (e: any) {
      notify.error(typeof e === 'string' ? e : e?.message || 'Не получилось');
    }
  };

  const handleSendPush = (test: ABTestListItem) => {
    const audience =
      test.segment_id ? `сегменту #${test.segment_id}` : 'всей подходящей аудитории';
    confirm({
      title: `Разослать пуши по тесту «${test.name}»?`,
      content: (
        <Space direction="vertical">
          <Text>
            Пуши уйдут {audience}. Пользователи распределятся по вариантам согласно процентам
            трафика; те, кто уже получал пуш по этому тесту, будут пропущены.
          </Text>
          <Text type="secondary">
            Рассылка выполняется в фоне — прогресс виден на странице результатов.
          </Text>
        </Space>
      ),
      okText: 'Разослать',
      cancelText: 'Отмена',
      width: 520,
      onOk: () => run(sendABTestPush(test.id), 'Рассылка запущена'),
    });
  };

  const handleDelete = (test: ABTestListItem) => {
    confirm({
      title: `Удалить тест «${test.name}»?`,
      content: 'Варианты, статистика и история отправок будут удалены безвозвратно.',
      okText: 'Удалить',
      okType: 'danger',
      cancelText: 'Отмена',
      onOk: () => run(deleteABTest(test.id), 'Тест удалён'),
    });
  };

  const actionsMenu = (record: ABTestListItem): MenuProps['items'] => {
    const items: MenuProps['items'] = [];

    if (record.status === ABTestStatus.DRAFT || record.status === ABTestStatus.PAUSED) {
      items.push({
        key: 'start',
        icon: <PlayCircleOutlined />,
        label: 'Запустить',
        onClick: () => run(startABTest(record.id), 'Тест запущен'),
      });
      items.push({
        key: 'edit',
        icon: <EditOutlined />,
        label: 'Редактировать',
        onClick: () => navigate(ROUTES.AB_TEST_EDIT.replace(':id', String(record.id))),
      });
    }

    if (record.status === ABTestStatus.RUNNING) {
      items.push({
        key: 'send',
        icon: <SendOutlined />,
        label: 'Разослать пуши',
        onClick: () => handleSendPush(record),
      });
      items.push({
        key: 'pause',
        icon: <PauseCircleOutlined />,
        label: 'Поставить на паузу',
        onClick: () => run(pauseABTest(record.id), 'Тест на паузе'),
      });
    }

    items.push({
      key: 'results',
      icon: <BarChartOutlined />,
      label: 'Результаты',
      onClick: () => navigate(ROUTES.AB_TEST_RESULTS.replace(':id', String(record.id))),
    });

    items.push({
      key: 'duplicate',
      icon: <CopyOutlined />,
      label: 'Дублировать',
      onClick: () => run(duplicateABTest({ id: record.id }), 'Копия создана'),
    });

    if (record.status !== ABTestStatus.RUNNING) {
      items.push({ type: 'divider' });
      items.push({
        key: 'delete',
        icon: <DeleteOutlined />,
        label: 'Удалить',
        danger: true,
        onClick: () => handleDelete(record),
      });
    }

    return items;
  };

  const columns: ColumnsType<ABTestListItem> = [
    {
      title: 'Название',
      dataIndex: 'name',
      key: 'name',
      width: 260,
      fixed: 'left',
      render: (name: string, record) => (
        <div>
          <Space size={6}>
            <ExperimentOutlined style={{ color: '#1890ff' }} />
            <Text strong>{name}</Text>
          </Space>
          {record.description && (
            <div>
              <Text type="secondary" style={{ fontSize: 12 }}>
                {formatters.truncate(record.description, 60)}
              </Text>
            </div>
          )}
        </div>
      ),
    },
    {
      title: 'Статус',
      dataIndex: 'status',
      key: 'status',
      width: 120,
      filterMultiple: false,
      filteredValue: statusFilter ? [statusFilter] : null,
      filters: Object.entries(STATUS_META).map(([value, meta]) => ({
        text: meta.label,
        value,
      })),
      render: (status: ABTestStatus) => {
        const meta = STATUS_META[status] || { color: 'default', label: status };
        return <Tag color={meta.color}>{meta.label}</Tag>;
      },
    },
    {
      title: 'Варианты',
      key: 'variants',
      width: 230,
      render: (_, record) => {
        const variants = record.variants || [];
        return (
          <Space direction="vertical" size={2} style={{ width: '100%' }}>
            {variants.slice(0, 3).map((v) => (
              <div key={v.id} style={{ fontSize: 12 }}>
                <Badge
                  color={v.is_control ? 'blue' : 'green'}
                  text={
                    <span>
                      {v.name} · {v.traffic_percentage}%
                      {v.sent > 0 && (
                        <Text type="secondary"> — CTR {v.ctr.toFixed(2)}%</Text>
                      )}
                    </span>
                  }
                />
              </div>
            ))}
            {variants.length > 3 && (
              <Text type="secondary" style={{ fontSize: 11 }}>
                +{variants.length - 3} ещё
              </Text>
            )}
          </Space>
        );
      },
    },
    {
      title: 'Отправлено',
      key: 'sent',
      width: 140,
      align: 'right',
      sorter: (a, b) => a.total_sent - b.total_sent,
      render: (_, record) =>
        record.total_sent > 0 ? (
          <Space direction="vertical" size={0} style={{ alignItems: 'flex-end' }}>
            <Text>{fmtInt(record.total_sent)}</Text>
            <Text type="secondary" style={{ fontSize: 12 }}>
              CTR {record.ctr.toFixed(2)}%
            </Text>
          </Space>
        ) : (
          <Text type="secondary">не рассылался</Text>
        ),
    },
    {
      title: 'Аудитория',
      key: 'audience',
      width: 170,
      render: (_, record) => {
        const chips: string[] = [];
        if (record.segment_id)
          chips.push(record.segment_name || `Сегмент #${record.segment_id}`);
        if (record.locale) chips.push(record.locale.toUpperCase());
        if (record.os) chips.push(record.os);
        if (record.offer) chips.push(record.offer);
        if (record.has_registration !== null)
          chips.push(record.has_registration ? 'Рег+' : 'Рег−');
        if (record.has_deposit !== null) chips.push(record.has_deposit ? 'Деп+' : 'Деп−');

        if (!chips.length) return <Text type="secondary">Все пользователи</Text>;
        return (
          <Space size={4} wrap>
            {chips.map((c, i) => (
              <Tag key={i}>{c}</Tag>
            ))}
          </Space>
        );
      },
    },
    {
      title: 'Победитель',
      key: 'winner',
      width: 170,
      render: (_, record) => {
        if (record.status !== ABTestStatus.COMPLETED || !record.winner_variant_id) {
          return <Text type="secondary">—</Text>;
        }
        const name =
          record.winner_name ||
          record.variants?.find((v) => v.id === record.winner_variant_id)?.name ||
          `#${record.winner_variant_id}`;
        return (
          <Space direction="vertical" size={0}>
            <Space size={4}>
              <TrophyOutlined style={{ color: '#faad14' }} />
              <Text strong>{name}</Text>
            </Space>
            {record.confidence_level > 0 && (
              <Tooltip title="Уверенность по z-тесту долей">
                <Progress
                  percent={Math.round(record.confidence_level)}
                  size="small"
                  showInfo={false}
                  strokeColor={record.confidence_level >= 95 ? '#52c41a' : '#faad14'}
                  style={{ width: 90 }}
                />
              </Tooltip>
            )}
          </Space>
        );
      },
    },
    {
      title: 'Период',
      key: 'period',
      width: 170,
      render: (_, record) => (
        <Text style={{ fontSize: 12 }}>
          {formatters.date(record.start_date)}
          {record.end_date && ` — ${formatters.date(record.end_date)}`}
        </Text>
      ),
    },
    {
      title: '',
      key: 'actions',
      width: 90,
      fixed: 'right',
      render: (_, record) => (
        <Space>
          <Tooltip title="Результаты">
            <Button
              type="text"
              icon={<EyeOutlined />}
              onClick={() =>
                navigate(ROUTES.AB_TEST_RESULTS.replace(':id', String(record.id)))
              }
            />
          </Tooltip>
          <Dropdown menu={{ items: actionsMenu(record) }} trigger={['click']}>
            <Button type="text" icon={<MoreOutlined />} />
          </Dropdown>
        </Space>
      ),
    },
  ];

  if (!appId) {
    return (
      <Card>
        <Empty
          image={<ExperimentOutlined style={{ fontSize: 56, color: '#d9d9d9' }} />}
          description="Выберите приложение, чтобы увидеть его A/B тесты"
        />
      </Card>
    );
  }

  return (
    <div>
      <div
        style={{
          marginBottom: 16,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          gap: 16,
          flexWrap: 'wrap',
        }}
      >
        <div>
          <Text strong style={{ fontSize: 16 }}>
            A/B тестирование
          </Text>
          <div>
            <Text type="secondary">
              Сравнивайте варианты пушей и раскатывайте тот, что реально работает
            </Text>
          </div>
        </div>
        {permissions.canManagePush && (
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={() => navigate(ROUTES.AB_TEST_CREATE)}
            size="large"
          >
            Создать A/B тест
          </Button>
        )}
      </div>

      <Table
        columns={columns}
        dataSource={tests}
        loading={loading || sending}
        rowKey="id"
        size="middle"
        scroll={{ x: 1400 }}
        onChange={(pagination, filters) => {
          const status = (filters.status as string[] | undefined)?.[0];
          if (status !== statusFilter) dispatch(setStatusFilter(status));
          dispatch(
            setPage({
              page: pagination.current || 1,
              pageSize: pagination.pageSize || 20,
            })
          );
        }}
        pagination={{
          current: page,
          pageSize,
          total,
          showSizeChanger: true,
          pageSizeOptions: ['20', '50', '100'],
          showTotal: (t) => `Всего тестов: ${t}`,
        }}
        locale={{
          emptyText: (
            <Empty
              image={<ExperimentOutlined style={{ fontSize: 48, color: '#d9d9d9' }} />}
              description={
                <Space direction="vertical" size={4}>
                  <Text>A/B тестов пока нет</Text>
                  <Text type="secondary">
                    Начните с двух вариантов заголовка — это самая быстрая проверка гипотезы
                  </Text>
                </Space>
              }
            />
          ),
        }}
      />
    </div>
  );
};

export default ABTestList;